import * as React from 'react'
import {
  NavigationContainerRefContext,
  NavigationContext,
  NavigationProp,
  NavigationRouteContext
} from './NavigationBuilder'
import { Route } from './routers'

/** The navigation object of the closest screen (or the container outside any screen). */
export function useNavigation<T = NavigationProp>(): T {
  const root = React.useContext(NavigationContainerRefContext)
  const navigation = React.useContext(NavigationContext)

  if (navigation === undefined && root === undefined) {
    throw new Error(
      "Couldn't find a navigation object. Is your component inside NavigationContainer?"
    )
  }

  return (navigation ?? root) as any
}

/** The route object of the closest screen. */
export function useRoute<T extends Route = Route>(): T {
  const route = React.useContext(NavigationRouteContext)

  if (route === undefined) {
    throw new Error(
      "Couldn't find a route object. Is your component inside a screen in a navigator?"
    )
  }

  return route as T
}

/** Whether the closest screen is focused; re-renders when that changes. */
export function useIsFocused(): boolean {
  const navigation = useNavigation()
  const [isFocused, setIsFocused] = React.useState(navigation.isFocused)

  const valueToReturn = navigation.isFocused()

  if (isFocused !== valueToReturn) {
    // If the value has changed since the last render, we need to update it.
    // This could happen if we missed an update from the event listeners during re-render.
    setIsFocused(valueToReturn)
  }

  React.useEffect(() => {
    const unsubscribeFocus = navigation.addListener('focus', () =>
      setIsFocused(true)
    )

    const unsubscribeBlur = navigation.addListener('blur', () =>
      setIsFocused(false)
    )

    return () => {
      unsubscribeFocus()
      unsubscribeBlur()
    }
  }, [navigation])

  return valueToReturn
}
